import { useNavigate } from "react-router-dom";
import { LogOut, MapPin, MapPinOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT, LANGS } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fmtTime } from "@/lib/api";

export const Brand = ({ light = false }) => {
  const { t } = useT();
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <img src="/images/logo.jpg" alt="Kon V Palto" className="h-10 w-10 rounded-full object-cover bg-white border-2 border-white shadow-sm" />
      <div className="leading-none">
        <div className={`font-display text-xl font-bold tracking-tight ${light ? "text-white" : "text-[#2c1810]"}`}>{t("app.name")}</div>
        <div className={`font-mono-kv text-[10px] uppercase tracking-[0.25em] ${light ? "text-amber-100" : "text-[#99432b]"}`}>{t("app.sub")}</div>
      </div>
    </div>
  );
};

export const LangSwitcher = () => {
  const { lang, setLang } = useT();
  return (
    <div className="flex rounded-full border border-[#E8DFD0] bg-white/80 p-0.5" data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`px-2.5 py-1 text-xs font-mono-kv uppercase rounded-full transition-colors ${lang === l ? "bg-[#2c1810] text-amber-50" : "text-stone-600 hover:text-[#b8563a]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[#FAF6F0]/85 border-b border-[#E8DFD0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Brand />
        <div className="flex items-center gap-2 sm:gap-4">
          <LangSwitcher />
          <div className="hidden sm:block text-right leading-tight">
            <div className="text-sm font-semibold" data-testid="header-user-name">{user.name}</div>
            <div className="text-xs text-stone-500">{user.role === "admin" ? t("roles.admin") : t(`positions.${user.position}`)}</div>
          </div>
          <Button variant="ghost" size="icon" data-testid="logout-button" aria-label={t("common.logout")}
            onClick={async () => { await logout(); nav("/login"); }}>
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
};

const POS_COLORS = {
  asci: "bg-orange-50 text-orange-800 border-orange-200",
  asci_yardimcisi: "bg-amber-50 text-amber-800 border-amber-200",
  sanat_hocasi: "bg-rose-50 text-rose-800 border-rose-200",
  barista_head: "bg-emerald-50 text-emerald-800 border-emerald-200",
  barista: "bg-teal-50 text-teal-800 border-teal-200",
  temizlik: "bg-sky-50 text-sky-800 border-sky-200",
  yonetici: "bg-stone-100 text-stone-800 border-stone-300",
};

export const PositionBadge = ({ position }) => {
  const { t } = useT();
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${POS_COLORS[position] || ""}`}>{t(`positions.${position}`)}</span>;
};

const STATUS_COLORS = {
  present: "bg-[#e5ede5] text-[#2d4232]", late: "bg-amber-100 text-amber-900", absent: "bg-red-100 text-red-800",
  not_arrived: "bg-red-50 text-red-700", upcoming: "bg-stone-100 text-stone-600", leave: "bg-violet-100 text-violet-800",
  off: "bg-stone-100 text-stone-500", extra: "bg-orange-100 text-orange-800",
};

export const StatusBadge = ({ status, testid }) => {
  const { t } = useT();
  return <span data-testid={testid} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLORS[status]}`}>{t(`status.${status}`)}</span>;
};

export const CorrectedNote = ({ r }) => {
  const { t } = useT();
  if (!r.corrected) return null;
  return (
    <div className="mt-1 inline-flex flex-col rounded-lg bg-sky-50 border border-sky-200 px-2 py-1 text-[11px] text-sky-900 font-sans" data-testid={`corrected-${r.id}`}>
      <span className="font-semibold">✓ {t("req.corrected")}</span>
      {r.original_clock_in && <span className="opacity-80">{t("req.original")}: {fmtTime(r.original_clock_in)} – {r.original_clock_out ? fmtTime(r.original_clock_out) : "…"}</span>}
      {r.corrected_note && <span className="opacity-80 italic">“{r.corrected_note}”</span>}
    </div>
  );
};

export const GeoBadge = ({ verified, distance, testid, manual }) => {
  const { t } = useT();
  if (manual) {
    return <span data-testid={testid} className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-sky-100 text-sky-800">{t("req.manual")}</span>;
  }
  const Icon = verified ? MapPin : MapPinOff;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${verified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
      <Icon className="h-3.5 w-3.5" />
      {verified ? t("clock.verified") : t("clock.outside")}
      {distance != null && <span className="font-mono-kv font-normal opacity-80">· {distance} m</span>}
    </span>
  );
};

export { TimeInput } from "./TimePicker";

export const Bar = ({ done, total, color = "bg-[#b8563a]", testid }) => {
  const { lang } = useT();
  const pct = total ? Math.round((done * 100) / total) : 0;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="h-2 flex-1 rounded-full bg-[#F3EDE2] overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-[width] duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono-kv text-xs w-10 text-right">{lang === "tr" ? `%${pct}` : `${pct}%`}</span>
    </div>
  );
};

export const PageTitle = ({ kicker, title, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 kv-rise">
    <div>
      {kicker && <div className="font-mono-kv text-xs uppercase tracking-[0.2em] text-[#99432b] mb-1">{kicker}</div>}
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#2c1810]">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const Loader = () => (
  <div className="py-20 grid place-items-center text-[#b8563a]" data-testid="loader">
    <Loader2 className="h-7 w-7 animate-spin" />
  </div>
);

export const Card = ({ className = "", children, ...rest }) => (
  <div className={`kv-card ${className}`} {...rest}>{children}</div>
);
